import { Box, Divider } from '@mui/material';
import { useSearch } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Query } from 'src/components/atoms/query';
import Page from 'src/components/organisms/page/page';
import { Pagination } from 'src/components/organisms/pagination';
import { useMeilisearchWithUrlParams } from 'src/hooks/meilisearch/use-meilisearch-with-url-params';
import { useDialog } from 'src/hooks/use-dialog';
import { useSortController } from 'src/hooks/use-sort-controller';
import { withPermissions } from 'src/permissions/can';
import { useUserStore } from 'src/store/user-store';
import { SortOrder } from 'src/types/enum/sort-order';
import { IssuesActionsSlotPayload } from '../molecules/issues-table-row';
import IssuesTable from '../organisms/issues-table';
import { IssuesActionsTableMenu } from '../molecules/issues-actions-table-menu';
import { SpaceChangeFilterPopover } from '../molecules/filters/space-change-filter-popover';
import { IssueStateFilterPopover } from '../molecules/filters/issue-state-filter-popover';
import { IssuesFilterChips } from '../molecules/filters/issues-filter-chips';
import EditIssueStatusDialog from '../organisms/dialogs/edit-issue-status-dialog';
import { fetchIssues } from '../../api/lib/issues';
import { issuesKeys } from '../../api/query-keys/issues';

const Issues = () => {
  const { t } = useTranslation(['issues', 'common']);

  const { directoryToken } = useUserStore();

  const { query, perPage, page, filter } = useSearch({
    from: '/_layout/tenant/issues/',
    select(state) {
      return {
        page: state.page,
        perPage: state.limit,
        query: state.q,
        filter: state.filter,
      };
    },
    structuralSharing: true,
  });

  const [sortController, setSortController] = useSortController({
    sortBy: 'reportedAt',
    sortOrder: SortOrder.ASC,
  });

  const defaultSearchStateConfig = useMemo(() => {
    return {
      sort: [`${sortController.sortBy}:${sortController.sortOrder}`],
    };
  }, [sortController.sortBy, sortController.sortOrder]);

  const {
    results,
    handleChangeQuery,
    handleChangePage,
    isLoading,
    isFetching,
    handleChangeLimit,
    error,
    handleChangeFilterValue,
    handleChangeSort,
  } = useMeilisearchWithUrlParams(
    fetchIssues,
    issuesKeys.list(directoryToken ?? '', page, perPage),
    defaultSearchStateConfig,
  );

  const [
    isOpenEditIssueStatusDialog,
    handleOpenEditIssueStatusDialog,
    handleCloseEditIssueStatusDialog,
  ] = useDialog();

  // const [
  //   isOpenExportIssuesAsXlsDialog,
  //   _handleOpenExportIssuesAsXlsDialog,
  //   handleCloseExportIssuesAsXlsDialog,
  // ] = useDialog();

  const [selectedIssue, setSelectedIssue] =
    useState<IssuesActionsSlotPayload | null>(null);

  const actionsSlot = useCallback(
    ({ token, status, comment }: IssuesActionsSlotPayload) => (
      <IssuesActionsTableMenu
        onEditStatus={() => {
          setSelectedIssue({ token, status, comment });
          handleOpenEditIssueStatusDialog();
        }}
        state={status}
      />
    ),
    [],
  );

  const totalPages = Math.ceil((results?.estimatedTotalHits ?? 0) / perPage);

  return (
    <>
      <Page>
        <Page.Header title={t('issues:common.title')}>
          {/* TODO: Add download functionality when api is ready */}
          {/* <Can I="download" a="Issues" passThrough>
          {allowed => (
            <Tooltip
              title={t('common:notSufficientPermissions')}
              disableHoverListener={allowed}
              disableFocusListener={allowed}>
              <span>
                <Button
                  onClick={handleOpenExportBookingsAsXlsDialog}
                  color="primary"
                  disabled={!allowed}
                  startIcon={<DownloadIcon fontSize="small" />}
                  size="large"
                  variant="outlinedContained"
                  id="download_bookings_data_button">
                  {t('bookings:common.downloadButtonText')}
                </Button>
              </span>
            </Tooltip>
          )}
        </Can> */}
        </Page.Header>

        <Page.ContentContainer>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              px: 3,
              py: 2.5,
            }}>
            <Query
              searchAsType
              onChange={handleChangeQuery}
              sx={{
                minWidth: 200,
              }}
              value={query}
            />
            <SpaceChangeFilterPopover
              filters={filter}
              handleChangeFilterValue={handleChangeFilterValue}
            />
            <IssueStateFilterPopover
              filters={filter}
              handleChangeFilterValue={handleChangeFilterValue}
            />
          </Box>
          <IssuesFilterChips
            filters={filter}
            handleChangeFilterValue={handleChangeFilterValue}
          />

          <IssuesTable
            data={results?.hits ?? []}
            isLoading={isLoading}
            error={error?.response?.data?.message}
            sortController={sortController}
            setSortController={setSortController}
            handleChangeSort={handleChangeSort}
            actionsSlot={actionsSlot}
          />

          <Divider sx={{ mt: 'auto' }} />

          <Pagination
            disabled={isLoading}
            isFetching={isFetching}
            changePage={handleChangePage}
            rowsCount={results?.estimatedTotalHits || 0}
            page={page}
            perPage={perPage}
            totalPages={totalPages}
            handlePerPageChange={handleChangeLimit}
          />
        </Page.ContentContainer>
      </Page>

      <EditIssueStatusDialog
        open={isOpenEditIssueStatusDialog}
        onCancel={handleCloseEditIssueStatusDialog}
        token={selectedIssue?.token ?? ''}
        currentStatus={selectedIssue?.status || 'NEW'}
        currentComment={selectedIssue?.comment || ''}
      />

      {/* <ExportIssuesAsXlsDialog
        open={isOpenExportIssuesAsXlsDialog}
        onCancel={handleCloseExportIssuesAsXlsDialog}
      /> */}
    </>
  );
};

export default withPermissions(
  permissions => permissions.cannot('read', 'Issues'),
  Issues,
);
